/**
 * Bexio payment reconciliation / open-rent dashboard section (B1c.3.1b).
 *
 * READ-ONLY: this component renders the GET /api/admin/bexio/reconciliation
 * response and NOTHING else. It never mutates, never maps, never triggers a
 * Bexio sync. The only backend interaction is the parent's reconciliation fetch;
 * an optional retry re-fetches that same GET (never a sync).
 *
 * Money values arrive as JSON decimal strings ("1500.00", signed "-100.00");
 * fmtMoney parses them without clamping so overpayments stay visible.
 *
 * Coverage is Option A (SAFETY_HOOKS §9.10): mapped_quad is the RAW full FK quad;
 * invalid_mapped_or_unresolved is a SUB-count shown as "davon", never subtracted
 * and never a separate bucket in the total equation.
 *
 * Self-contained presentational primitives (local fmt/Badge/Card) match the
 * existing AdminBexioPage dark-theme style; they are intentionally NOT imported
 * from the page (which does not export them) to keep this section isolated.
 */
import React from "react";

function fmtMoney(v, currency) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  // Signed — negative (possible overpayment) is preserved, never clamped.
  return `${currency || "CHF"} ${n.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("de-CH");
  } catch {
    return "—";
  }
}

function fmtDateTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("de-CH", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

const STATUS_LABEL = {
  draft: "Entwurf",
  pending: "ausstehend",
  paid: "bezahlt",
  partial: "teilweise bezahlt",
  canceled: "abgesagt",
  cancelled: "abgesagt",
  unknown: "unbekannt",
};

function Badge({ children, tone = "neutral" }) {
  const tones = {
    neutral: "border-input text-muted-foreground",
    good: "border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400",
    warn: "border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300",
    bad: "border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em] ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-[10px] border border-border bg-card p-4">
      <div className="text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-[18px] font-bold text-foreground">{value}</div>
    </div>
  );
}

// ── Freshness banner ────────────────────────────────────────────────
function FreshnessBanner({ freshness, syncStale }) {
  const reason = syncStale?.reason;
  const last = freshness?.last_successful_invoices_sync;
  const threshold = freshness?.threshold_hours ?? 30;
  // Single text nodes (template literals) so the banner strings are matchable as
  // one piece, not split across {…} interpolations.
  if (reason === "no_successful_invoices_sync") {
    return (
      <div className="mb-4 rounded-[10px] border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-3 text-[12px] text-red-700 dark:text-red-300">
        Kein erfolgreicher Rechnungs-Sync gefunden — die Zahlen können unvollständig sein.
      </div>
    );
  }
  if (freshness?.is_stale || reason === "stale_gt_30h") {
    return (
      <div className="mb-4 rounded-[10px] border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-[12px] text-amber-800 dark:text-amber-300">
        {`Rechnungs-Sync veraltet (älter als ${threshold} h). Letzter Erfolg: ${fmtDateTime(last)}`}
      </div>
    );
  }
  return (
    <div className="mb-4 text-[11px] text-muted-foreground">
      {`Rechnungs-Sync aktuell. Letzter Erfolg: ${fmtDateTime(last)}`}
    </div>
  );
}

// ── Mapping coverage card (Option A) ────────────────────────────────
function CoverageCard({ coverage }) {
  const c = coverage || {};
  return (
    <div className="rounded-[10px] border border-border bg-card p-4">
      <div className="text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
        Mapping-Abdeckung
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] text-foreground md:grid-cols-4">
        <div>
          <span className="text-muted-foreground">Total:</span>{" "}
          <span className="font-semibold text-foreground">{c.total ?? "—"}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Zugeordnet:</span>{" "}
          <span className="font-semibold text-foreground">{c.mapped_quad ?? "—"}</span>
          {/* Option A: a SUB-count of mapped_quad, shown as "davon" — never
              subtracted, never a separate bucket in the total equation. */}
          <div className="text-[10px] text-muted-foreground">
            {`davon nicht auflösbar: ${c.invalid_mapped_or_unresolved ?? 0}`}
          </div>
        </div>
        <div>
          <span className="text-muted-foreground">Nicht zugeordnet:</span>{" "}
          <span className="font-semibold text-foreground">{c.unmapped ?? "—"}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Teilweise / prüfen:</span>{" "}
          <span className="font-semibold text-foreground">
            {c.partial_maybe_invalid ?? "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Warning panel ───────────────────────────────────────────────────
function WarningPanel({ warnings, currencyWarning, syncStale }) {
  const w = warnings || {};
  const groups = [
    {
      key: "invalid_mapped_invoices",
      label: "Ungültige / nicht auflösbare Zuordnungen",
      explain:
        "Voller FK-Quad, aber eine referenzierte Zeile ist nicht auflösbar. Bleibt in den Summen — Datenqualität prüfen.",
      items: w.invalid_mapped_invoices,
      render: (it) =>
        `${it.document_nr || `#${it.bexio_invoice_id}`} · fehlend: ${
          (it.unresolved_fields || []).join(", ") || "—"
        }`,
    },
    {
      key: "canceled_with_payment_rows",
      label: "Stornierte Rechnungen mit Zahlungszeilen",
      explain: "Geld floss trotz Stornierung — Refund/Buchhaltung prüfen.",
      items: w.canceled_with_payment_rows,
      render: (it) =>
        `${it.document_nr || `#${it.bexio_invoice_id}`} · Zahlungen: ${
          it.payment_rows ?? "?"
        } (${fmtMoney(it.payment_sum, "CHF")})`,
    },
    {
      key: "total_paid_differs_from_payment_sum",
      label: "total_paid weicht von Zahlungssumme ab",
      explain: "Mögliche stale Payment-Row / Gebühr / Rundung.",
      items: w.total_paid_differs_from_payment_sum,
      render: (it) =>
        `${it.document_nr || `#${it.bexio_invoice_id}`} · bezahlt ${fmtMoney(
          it.total_paid,
          "CHF"
        )} vs. Zahlungen ${fmtMoney(it.payment_sum, "CHF")}`,
    },
    {
      key: "paid_without_payment_rows",
      label: "Bezahlt, aber keine Zahlungszeilen",
      explain: "Als bezahlt markiert, keine Payment-Rows im Spiegel.",
      items: w.paid_without_payment_rows,
      render: (it) => `${it.document_nr || `#${it.bexio_invoice_id}`}`,
    },
    {
      key: "pending_with_payment_rows",
      label: "Offen, aber Zahlungszeilen vorhanden",
      explain: "Status offen/pending trotz vorhandener Zahlungen.",
      items: w.pending_with_payment_rows,
      render: (it) =>
        `${it.document_nr || `#${it.bexio_invoice_id}`} · offen ${fmtMoney(
          it.total_remaining,
          "CHF"
        )}`,
    },
    {
      key: "excluded_mapped_invoices",
      label: "Ausgeschlossene zugeordnete Rechnungen",
      explain: "Entwurf/storniert/unbekannt — nicht in Summen gezählt.",
      items: w.excluded_mapped_invoices,
      render: (it) =>
        `${it.document_nr || `#${it.bexio_invoice_id}`} · Status: ${
          STATUS_LABEL[it.bexio_status] || it.bexio_status || "—"
        }`,
    },
    {
      key: "duplicate_risk_vantio_invoices",
      label: "Duplikat-Risiko (Vantio vs. Bexio)",
      explain:
        "Vantio-Rechnung mit ähnlichem Betrag — nur Kontext, nie summiert.",
      items: w.duplicate_risk_vantio_invoices,
      render: (it) =>
        `${it.bexio_document_nr || `#${it.bexio_invoice_id}`} ↔ ${
          it.vantio_invoice_number || "—"
        } · Δ ${fmtMoney(it.amount_diff, "CHF")} · ${it.date_window || "—"}`,
    },
  ].filter((g) => Array.isArray(g.items) && g.items.length > 0);

  const cw = currencyWarning || w.non_chf;
  const hasNonChf = !!cw?.has_non_chf;
  const staleReason = syncStale?.reason;
  const isStaleWarn = staleReason && staleReason !== "fresh";

  const isHealthy = groups.length === 0 && !hasNonChf && !isStaleWarn;

  if (isHealthy) {
    return (
      <div className="rounded-[10px] border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-3 text-[12px] text-emerald-700 dark:text-emerald-400">
        ✓ Keine Auffälligkeiten
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <div
          key={g.key}
          className="rounded-[10px] border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3"
        >
          <div className="flex items-center gap-2">
            <Badge tone="warn">{g.items.length}</Badge>
            <span className="text-[12px] font-semibold text-foreground">
              {g.label}
            </span>
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">{g.explain}</div>
          <ul className="mt-2 space-y-0.5 text-[11px] text-foreground">
            {g.items.slice(0, 25).map((it, i) => (
              <li key={i}>{g.render(it)}</li>
            ))}
          </ul>
        </div>
      ))}

      {hasNonChf ? (
        <div className="rounded-[10px] border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3">
          <div className="flex items-center gap-2">
            <Badge tone="warn">{(cw.non_chf_document_nrs || []).length}</Badge>
            <span className="text-[12px] font-semibold text-foreground">
              Nicht-CHF-Rechnungen
            </span>
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            Fremdwährung erkannt (currency ≠ CHF).
          </div>
          <div className="mt-2 text-[11px] text-foreground">
            {(cw.non_chf_document_nrs || []).join(", ") || "—"}
          </div>
        </div>
      ) : null}

      {isStaleWarn ? (
        <div className="rounded-[10px] border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-[11px] text-amber-800 dark:text-amber-300">
          Sync-Status: {staleReason}
        </div>
      ) : null}
    </div>
  );
}

// ── Per-tenancy table ───────────────────────────────────────────────
function TenancyTable({ tenancies, currency }) {
  const rows = Array.isArray(tenancies) ? tenancies : [];
  if (rows.length === 0) {
    return (
      <div className="rounded-[10px] border border-border bg-card p-6 text-center text-[12px] text-muted-foreground">
        Keine zugeordneten Rechnungen.
      </div>
    );
  }
  const head = [
    "Mieter / Referenz",
    "Status",
    "Einheit / Zimmer",
    "Gezählt",
    "Offen fällig",
    "Offen n. fällig",
    "Offen total",
    "Bezahlt",
    "Fakturiert",
    "Hinweise",
  ];
  return (
    <div className="overflow-x-auto rounded-[10px] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-card">
            {head.map((h, i) => (
              <th
                key={i}
                className="whitespace-nowrap px-3 py-2 text-left text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const unresolved = !r.tenant_display_name;
            const docs = Array.isArray(r.counted_documents)
              ? r.counted_documents
              : [];
            return (
              <tr
                key={r.tenancy_id}
                className="border-t border-border align-top text-foreground"
              >
                <td className="px-3 py-2">
                  {unresolved ? (
                    <Badge tone="bad">Zuordnung nicht auflösbar</Badge>
                  ) : (
                    <div className="font-semibold text-foreground">
                      {r.tenant_display_name}
                    </div>
                  )}
                  <div className="text-[10px] text-muted-foreground">
                    {r.tenancy_reference_number || "—"}
                  </div>
                  {docs.length ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {docs.slice(0, 12).map((d) => (
                        <span
                          key={d.bexio_invoice_id}
                          className="rounded-[6px] border border-input px-1.5 py-0.5 text-[9px] text-muted-foreground"
                          title={`fällig ${fmtDate(d.due_date)}`}
                        >
                          {d.document_nr || `#${d.bexio_invoice_id}`} ·{" "}
                          {STATUS_LABEL[d.bexio_status] || d.bexio_status} ·{" "}
                          {fmtMoney(d.total_remaining, currency)}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  {r.tenancy_status ? (
                    <Badge>{r.tenancy_status}</Badge>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-3 py-2">
                  <div>{r.unit_label || "—"}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.room_label || "—"}
                  </div>
                </td>
                <td className="px-3 py-2">{r.counted_invoices ?? 0}</td>
                <td className="whitespace-nowrap px-3 py-2">
                  {fmtMoney(r.open_due, currency)}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {fmtMoney(r.open_not_yet_due_or_no_due_date, currency)}
                </td>
                <td className="whitespace-nowrap px-3 py-2 font-semibold text-foreground">
                  {fmtMoney(r.open_total, currency)}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {fmtMoney(r.paid_total_invoice_level, currency)}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {fmtMoney(r.invoice_total_counted, currency)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {r.mapped_canceled_invoices > 0 ? (
                      <Badge tone="bad">
                        {r.mapped_canceled_invoices} storniert
                      </Badge>
                    ) : null}
                    {r.mapped_draft_invoices > 0 ? (
                      <Badge tone="warn">{r.mapped_draft_invoices} Entwurf</Badge>
                    ) : null}
                    {r.mapped_unknown_status_invoices > 0 ? (
                      <Badge tone="warn">
                        {r.mapped_unknown_status_invoices} unbekannt
                      </Badge>
                    ) : null}
                    {unresolved ? <Badge tone="bad">Datenqualität prüfen</Badge> : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Main section ────────────────────────────────────────────────────
export default function BexioReconciliationSection({
  reconciliation,
  loading,
  error,
  onRetry,
}) {
  const malformed =
    reconciliation &&
    (!reconciliation.summary ||
      !reconciliation.mapping_coverage ||
      !reconciliation.status_counts);
  const isError = !!error || (!loading && (!reconciliation || malformed));

  return (
    <section className="mb-6">
      <div className="mb-2">
        <h2 className="text-[14px] font-semibold text-foreground">
          Bexio Zahlungsabgleich
        </h2>
        <p className="max-w-[720px] text-[11px] text-muted-foreground">
          Schreibgeschützter Abgleich aus dem Bexio-Spiegel. Bexio ist führend für
          offen/bezahlt.
          {!loading && !isError && reconciliation?.generated_at ? (
            <> · Stand: {fmtDateTime(reconciliation.generated_at)}</>
          ) : null}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 rounded-[10px] border border-border bg-card p-6 text-[12px] text-muted-foreground">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-input border-t-primary" />
          Lädt Zahlungsabgleich …
        </div>
      ) : isError ? (
        <div className="rounded-[10px] border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-[13px] text-red-700 dark:text-red-300">
          <div>
            Zahlungsabgleich konnte nicht geladen werden
            {error ? `: ${error}` : "."}
          </div>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 rounded-[8px] border border-red-200 dark:border-red-500/30 px-3 py-1 text-[12px] text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20"
            >
              Erneut versuchen
            </button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <FreshnessBanner
            freshness={reconciliation.freshness}
            syncStale={reconciliation.warnings?.sync_stale}
          />

          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            <StatCard
              label="Offen fällig"
              value={fmtMoney(
                reconciliation.summary.open_due,
                reconciliation.summary.currency
              )}
            />
            <StatCard
              label="Offen noch nicht fällig / ohne Fälligkeit"
              value={fmtMoney(
                reconciliation.summary.open_not_yet_due_or_no_due_date,
                reconciliation.summary.currency
              )}
            />
            <StatCard
              label="Offen total"
              value={fmtMoney(
                reconciliation.summary.open_total,
                reconciliation.summary.currency
              )}
            />
            <StatCard
              label="Bezahlt"
              value={fmtMoney(
                reconciliation.summary.paid_total_invoice_level,
                reconciliation.summary.currency
              )}
            />
            <StatCard
              label="Fakturiert total"
              value={fmtMoney(
                reconciliation.summary.invoice_total_counted,
                reconciliation.summary.currency
              )}
            />
          </div>

          <CoverageCard coverage={reconciliation.mapping_coverage} />

          <WarningPanel
            warnings={reconciliation.warnings}
            currencyWarning={reconciliation.currency_warning}
            syncStale={reconciliation.warnings?.sync_stale}
          />

          <TenancyTable
            tenancies={reconciliation.tenancies}
            currency={reconciliation.summary.currency}
          />
        </div>
      )}
    </section>
  );
}
